// src/components/trial/lp/trial-beta/sections/experience.tsx
// Ported from the design attachment, markup lines 3688-3852.
//
// Two departures from a literal port, both verified against the source and its CSS:
//   * The `<h2>` title is unchanged from the source (it was already an `<h2>` here, unlike
//     multiconnection/multi-internet's own sections — this page's `<h1>` still belongs to hero.tsx).
//   * The source's `svg[data-anim="exp-demo"]` (a static app-mock fallback, sibling of
//     `.np-exp-p1-client`) is NOT ported. It is permanently hidden by
//     `.trial-beta-page #experience .np-exp-p1-client + svg[data-anim='exp-demo'] { display:none
//     !important; }` (public/css/trial-beta.css) regardless of any page-state class — the CSS
//     comment right above that rule says the real client video "reuses the exact P1 video inside
//     the original 16:9 slot" and the mock "stays in the DOM only as a no-JS fallback" in the
//     source. Omitting it changes no CSS: no `:nth-child`/direct-child rule under `#experience`
//     depends on the media column's exact child count (verified with the grep the brief
//     prescribes for structural changes).
//
// The four `.np5-client-frame` figures ARE ported (the brief calls for them) but stay static: the
// source's own `.np5-client-video-mode` class — baked into this markup, never toggled by any
// script — hides them permanently in favour of the video (see ../animation/experience.ts's header
// comment for the verification). Rendering them costs nothing and keeps this section's DOM shape
// identical to the source's; animating them would not.
import { useTranslation } from 'next-i18next';

import { experienceAnimation } from '../animation/experience';
import { useSectionAnimation } from '../animation/use-section-animation';
import { TRIAL_BETA_ASSETS } from '../assets';

interface ExperienceProps {
  onOpenModal: () => void;
}

export function Experience({ onOpenModal }: ExperienceProps) {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(experienceAnimation);

  return (
    <section id='experience' ref={sectionRef} className='relative px-4 py-14 md:px-8 md:py-20' style={{ zIndex: 1 }}>
      <div className='pointer-events-none absolute inset-0' aria-hidden='true' style={{ background: 'linear-gradient(to bottom, transparent, #2ECDFF05, transparent)' }} />
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        <div className='mb-10 text-center md:mb-14'>
          <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: '#2ECDFF', fontFamily: 'var(--font-outfit)' }}>
            {t('experience.eyebrow')}
          </span>
          <h2 className='mb-4 text-3xl font-bold md:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
            {t('experience.title')} <span style={{ color: '#2ECDFF' }}>{t('experience.titleHighlight')}</span>
          </h2>
          <p className='mx-auto max-w-2xl text-base md:text-lg' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
            {t('experience.sub')}
          </p>
        </div>
        <div className='flex flex-col items-center gap-12 lg:flex-row'>
          <div className='relative mx-auto w-full max-w-2xl flex-1'>
            <div className='pointer-events-none absolute -inset-6 rounded-3xl bg-[#93FF18]/5 blur-3xl' />
            <div className='overflow-hidden rounded-2xl border shadow-2xl' style={{ borderColor: '#93FF1825', boxShadow: '0 0 40px #93FF1810' }}>
              <div className='np-exp-p1-client np5-client-video-mode' data-exp-p1-client data-client-scene='0' aria-label={t('experience.video.clientAriaLabel')}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative client-video loop:
                    muted, no audio track and aria-labelled; the source (3701) shipped it the same way. */}
                <video className='np5-client-video' data-np-client-video autoPlay muted loop playsInline preload='metadata' disablePictureInPicture poster={TRIAL_BETA_ASSETS.clientVideoPoster} aria-label={t('experience.video.playerAriaLabel')}>
                  <source src={TRIAL_BETA_ASSETS.clientVideo} type='video/mp4' />
                </video>
                <figure className='np5-client-frame is-active' data-exp-p1-frame='0'>
                  <img src={TRIAL_BETA_ASSETS.clientLibrary} alt={t('experience.frames.library')} />
                </figure>
                <figure className='np5-client-frame' data-exp-p1-frame='1'>
                  <img src={TRIAL_BETA_ASSETS.clientRoute} alt={t('experience.frames.route')} />
                </figure>
                <figure className='np5-client-frame' data-exp-p1-frame='2'>
                  <img src={TRIAL_BETA_ASSETS.clientConnected} alt={t('experience.frames.connected')} />
                </figure>
                <figure className='np5-client-frame' data-exp-p1-frame='3'>
                  <img src={TRIAL_BETA_ASSETS.clientTraffic} alt={t('experience.frames.traffic')} />
                </figure>
                <div className='np5-client-transition' data-exp-p1-transition />
                <div className='np5-client-cursor' data-exp-p1-cursor>
                  <i />
                </div>
                <div className='np5-client-ripple' data-exp-p1-ripple />
              </div>
            </div>
          </div>
          <div className='max-w-md flex-1 space-y-6'>
            <div data-anim='exp-feature' className='flex items-start gap-4'>
              <div className='np-exp-feature-icon flex size-10 shrink-0 items-center justify-center rounded-lg' style={{ background: '#93FF1815', border: '1px solid #93FF1825' }}>
                <svg data-exp-feature-icon='globe' viewBox='0 0 40 40' aria-hidden='true'>
                  <circle className='np-exp-globe-aura' cx={20} cy={20} r={18} />
                  <circle className='np-exp-globe-shell' cx={20} cy={20} r={14.5} />
                  <ellipse className='np-exp-globe-lat' cx={20} cy={20} rx={14.5} ry={5} />
                  <ellipse className='np-exp-globe-lat' cx={20} cy={20} rx={12.5} ry={10} />
                  <ellipse className='np-exp-globe-meridian' data-exp-globe-meridian data-phase='0' cx={20} cy={20} rx={5} ry={14.5} />
                  <ellipse className='np-exp-globe-meridian' data-exp-globe-meridian data-phase='60' cx={20} cy={20} rx={12} ry={14.5} />
                  <ellipse className='np-exp-globe-meridian' data-exp-globe-meridian data-phase='120' cx={20} cy={20} rx={12} ry={14.5} />
                  <g className='np-exp-globe-nodes'>
                    <circle data-exp-globe-node data-lat='-52' data-lon='8' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='-35' data-lon='74' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='-16' data-lon='138' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='4' data-lon='204' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='22' data-lon='266' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='39' data-lon='326' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='52' data-lon='170' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='10' data-lon='42' cx={20} cy={20} r={1} />
                    <circle data-exp-globe-node data-lat='-7' data-lon='302' cx={20} cy={20} r={1} />
                  </g>
                </svg>
              </div>
              <div>
                <h3 className='mb-1 text-lg font-bold text-white' style={{ fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.serversTitle')}
                </h3>
                <p className='text-sm' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.serversDescription')}
                </p>
              </div>
            </div>
            <div data-anim='exp-feature' className='flex items-start gap-4'>
              <div className='np-exp-feature-icon flex size-10 shrink-0 items-center justify-center rounded-lg' style={{ background: '#2ECDFF15', border: '1px solid #2ECDFF25' }}>
                <svg data-exp-feature-icon='bolt' viewBox='0 0 40 40' aria-hidden='true'>
                  <defs>
                    <linearGradient id='np-exp-bolt-gradient' x1={10} y1={36} x2={30} y2={3} gradientUnits='userSpaceOnUse'>
                      <stop stopColor='#2ECDFF' />
                      <stop offset={1} stopColor='#D8FF9D' />
                    </linearGradient>
                  </defs>
                  <path className='np-exp-bolt-halo' d='M24.5 3.5L11.5 21H19L15.5 36.5L29.5 17H22Z' />
                  <path className='np-exp-bolt' data-exp-bolt d='M24.5 3.5L11.5 21H19L15.5 36.5L29.5 17H22Z' />
                  <path className='np-exp-bolt-trace' data-exp-bolt-trace pathLength={100} d='M24.5 3.5L11.5 21H19L15.5 36.5L29.5 17H22Z' />
                  <circle className='np-exp-bolt-spark' data-exp-bolt-spark r={1.4} />
                  <circle className='np-exp-bolt-spark is-secondary' data-exp-bolt-spark r={1} />
                </svg>
              </div>
              <div>
                <h3 className='mb-1 text-lg font-bold text-white' style={{ fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.connectTitle')}
                </h3>
                <p className='text-sm' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.connectDescription')}
                </p>
              </div>
            </div>
            <div data-anim='exp-feature' className='flex items-start gap-4'>
              <div className='np-exp-feature-icon flex size-10 shrink-0 items-center justify-center rounded-lg' style={{ background: '#93FF1815', border: '1px solid #93FF1825' }}>
                <svg data-exp-feature-icon='stats' viewBox='0 0 40 40' aria-hidden='true'>
                  <path className='np-exp-stats-grid' d='M4 11H36M4 20H36M4 29H36M11 5V35M20 5V35M29 5V35' />
                  <g className='np-exp-stats-bars'>
                    <rect data-exp-stats-bar x={6} y={25} width={4} height={9} rx={1.5} />
                    <rect data-exp-stats-bar x={13} y={20} width={4} height={14} rx={1.5} />
                    <rect data-exp-stats-bar x={20} y={23} width={4} height={11} rx={1.5} />
                    <rect data-exp-stats-bar x={27} y={14} width={4} height={20} rx={1.5} />
                    <rect data-exp-stats-bar x={34} y={18} width={3} height={16} rx={1.5} />
                  </g>
                  <polyline className='np-exp-stats-line' data-exp-stats-line points='5,27 12,23 19,25 26,17 33,19 36,14' />
                  <circle className='np-exp-stats-dot' data-exp-stats-dot cx={36} cy={14} r={1.7} />
                </svg>
              </div>
              <div>
                <h3 className='mb-1 text-lg font-bold text-white' style={{ fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.statsTitle')}
                </h3>
                <p className='text-sm' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                  {t('experience.features.statsDescription')}
                </p>
              </div>
            </div>
            {/* Outfit 750/14px, matching the source's own `.cta { font: 750 14px/1 var(--body) }`
                and the Multiconnection and final CTAs. This button had inherited Bai at 400/16px,
                which is the pairing the card reports (SCRUM-1886). */}
            <div className='pt-4'>
              <button type='button' onClick={onOpenModal} className='inline-block cursor-pointer rounded-xl border-none px-8 py-4 text-sm font-bold' style={{ background: '#93FF18', color: '#080809', fontFamily: 'var(--font-outfit)', fontWeight: 750, fontSize: '14px', boxShadow: '0 0 20px #93FF1830' }}>
                {t('experience.cta')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
